import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { t } from "@chuzi/shared/config";
import type { StoryListItem, SceneListItem, StoryVisibilityTier } from "@chuzi/shared/types";
import { resolveStoryVisibilityTier } from "@chuzi/shared/types";
import { DefaultSceneSlate } from "./default-scene-slate";

interface StoryHoverHudProps {
  story: StoryListItem;
  scene: SceneListItem;
  /** Screen-space position (pixels) where the focused star projects. */
  screenX: number;
  screenY: number;
  realm: "cosmos" | "wilds";
  /** When set, only the control strip is shown (preview/labels live on 3D billboards). */
  variant?: "full" | "controlsOnly";
  /** Match preview card width for the 2×2 control grid. */
  previewWidth?: number;
  coverboxUrl?: string | null;
  creatorAvatarUrl?: string | null;
  canDelete?: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
  onLaunch?: () => void;
  onPublish?: () => void;
  onUnpublish?: () => void;
  onRetract?: () => void;
}

type DeleteState = "idle" | "confirming";

export interface SceneConstellationControlsProps {
  realm: "cosmos" | "wilds";
  previewWidth?: number;
  canDelete?: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
  onLaunch?: () => void;
  onPublish?: () => void;
  onUnpublish?: () => void;
  onRetract?: () => void;
  storyTier?: StoryVisibilityTier;
  storyPublished?: boolean;
}

/** Control strip for embedding beneath a 3D constellation preview card. */
export function SceneConstellationControls({
  realm,
  previewWidth = 148,
  canDelete = false,
  onDelete,
  onEdit,
  onLaunch,
  onPublish,
  onUnpublish,
  onRetract,
  storyTier,
  storyPublished = false,
}: SceneConstellationControlsProps) {
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120,0.7)" : "rgba(126,184,255,0.7)";
  const accentSolid = isWilds ? "#78c878" : "#7eb8ff";

  const [deleteState, setDeleteState] = useState<DeleteState>("idle");
  const [unpublishState, setUnpublishState] = useState<DeleteState>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unpublishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (unpublishTimerRef.current) clearTimeout(unpublishTimerRef.current);
    };
  }, []);

  const handleDeleteClick = useCallback(() => {
    if (deleteState === "idle") {
      setDeleteState("confirming");
      timerRef.current = setTimeout(() => {
        setDeleteState("idle");
        timerRef.current = null;
      }, 3000);
    } else if (deleteState === "confirming") {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setDeleteState("idle");
      onDelete?.();
    }
  }, [deleteState, onDelete]);

  const handleUnpublishClick = useCallback(() => {
    if (unpublishState === "idle") {
      setUnpublishState("confirming");
      unpublishTimerRef.current = setTimeout(() => {
        setUnpublishState("idle");
        unpublishTimerRef.current = null;
      }, 3000);
    } else if (unpublishState === "confirming") {
      if (unpublishTimerRef.current) {
        clearTimeout(unpublishTimerRef.current);
        unpublishTimerRef.current = null;
      }
      setUnpublishState("idle");
      onUnpublish?.();
    }
  }, [unpublishState, onUnpublish]);

  const tier = storyTier ?? (storyPublished ? "worldwide" : "private");
  const canLaunch = !!(onLaunch && tier === "worldwide");
  const canPublish = !!(onPublish && tier === "private");
  const canUnpublish = !!(onUnpublish && tier === "worldwide");
  const canRetract = !!(onRetract && tier === "limited");
  const showDelete = canDelete && !canUnpublish && !canRetract;
  const hasControls = !!(
    onEdit ||
    canLaunch ||
    showDelete ||
    canPublish ||
    canUnpublish ||
    canRetract
  );
  if (!hasControls) return null;

  return (
    <ControlStrip
      realm={realm}
      accent={accent}
      accentSolid={accentSolid}
      previewWidth={previewWidth}
      attached
      onEdit={onEdit}
      canLaunch={canLaunch}
      onLaunch={onLaunch}
      canPublish={canPublish}
      onPublish={onPublish}
      canUnpublish={canUnpublish}
      onUnpublishClick={handleUnpublishClick}
      unpublishState={unpublishState}
      canRetract={canRetract}
      onRetract={onRetract}
      canDelete={showDelete}
      deleteState={deleteState}
      onDeleteClick={handleDeleteClick}
    />
  );
}

export function StoryHoverHud({
  story,
  scene,
  screenX,
  screenY,
  realm,
  variant = "full",
  previewWidth = 148,
  coverboxUrl,
  creatorAvatarUrl,
  canDelete = false,
  onDelete,
  onEdit,
  onLaunch,
  onPublish,
  onUnpublish,
  onRetract,
}: StoryHoverHudProps) {
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120,0.7)" : "rgba(126,184,255,0.7)";
  const accentSolid = isWilds ? "#78c878" : "#7eb8ff";
  const chipBg = isWilds ? "rgba(90,180,90,0.22)" : "rgba(126,184,255,0.22)";
  const chipBorder = isWilds ? "rgba(90,180,90,0.5)" : "rgba(126,184,255,0.5)";

  const [deleteState, setDeleteState] = useState<DeleteState>("idle");
  const [unpublishState, setUnpublishState] = useState<DeleteState>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unpublishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (unpublishTimerRef.current) clearTimeout(unpublishTimerRef.current);
    };
  }, []);

  const handleDeleteClick = useCallback(() => {
    if (deleteState === "idle") {
      setDeleteState("confirming");
      timerRef.current = setTimeout(() => {
        setDeleteState("idle");
        timerRef.current = null;
      }, 3000);
    } else if (deleteState === "confirming") {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setDeleteState("idle");
      onDelete?.();
    }
  }, [deleteState, onDelete]);

  const handleUnpublishClick = useCallback(() => {
    if (unpublishState === "idle") {
      setUnpublishState("confirming");
      unpublishTimerRef.current = setTimeout(() => {
        setUnpublishState("idle");
        unpublishTimerRef.current = null;
      }, 3000);
    } else if (unpublishState === "confirming") {
      if (unpublishTimerRef.current) {
        clearTimeout(unpublishTimerRef.current);
        unpublishTimerRef.current = null;
      }
      setUnpublishState("idle");
      onUnpublish?.();
    }
  }, [unpublishState, onUnpublish]);

  const tier = resolveStoryVisibilityTier(story);
  const canLaunch = !!(onLaunch && tier === "worldwide");
  const canPublish = !!(onPublish && tier === "private");
  const canUnpublish = !!(onUnpublish && tier === "worldwide");
  const canRetract = !!(onRetract && tier === "limited");
  const showDelete = canDelete && !canUnpublish && !canRetract;
  const hasControls = !!(
    onEdit ||
    canLaunch ||
    showDelete ||
    canPublish ||
    canUnpublish ||
    canRetract
  );
  const controlsOnly = variant === "controlsOnly";

  if (controlsOnly && !hasControls) return null;

  return (
    <>
      <div
        style={{
          position: "absolute",
          left: screenX,
          top: screenY,
          transform: controlsOnly ? "translate(-50%, 0)" : "translate(-50%, -100%)",
          paddingBottom: controlsOnly ? 0 : 28,
          pointerEvents: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          zIndex: 20,
          animation: "hudFadeIn 0.4s ease-out",
        }}
      >
        {!controlsOnly ? (
          <CoverboxCard
            url={coverboxUrl ?? null}
            title={story.title}
            contentRating={story.content_rating}
            genre={story.genre}
            realm={realm}
            chipBg={chipBg}
            chipBorder={chipBorder}
            scene={scene}
            creator={story.creator}
            creatorAvatarUrl={creatorAvatarUrl ?? null}
          />
        ) : null}

        {hasControls ? (
          <ControlStrip
            realm={realm}
            accent={accent}
            accentSolid={accentSolid}
            previewWidth={previewWidth}
            onEdit={onEdit}
            canLaunch={canLaunch}
            onLaunch={onLaunch}
            canPublish={canPublish}
            onPublish={onPublish}
            canUnpublish={canUnpublish}
            onUnpublishClick={handleUnpublishClick}
            unpublishState={unpublishState}
            canRetract={canRetract}
            onRetract={onRetract}
            canDelete={showDelete}
            deleteState={deleteState}
            onDeleteClick={handleDeleteClick}
          />
        ) : null}
      </div>

      {!controlsOnly ? (
        <div
          style={{
            position: "absolute",
            left: screenX,
            top: screenY + 28,
            transform: "translateX(-50%)",
            pointerEvents: "none",
            zIndex: 20,
            animation: "hudFadeIn 0.4s ease-out",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: "rgba(232,240,255,0.5)",
              letterSpacing: 1,
              textTransform: "uppercase",
              textAlign: "center",
              textShadow: "0 2px 8px rgba(0,0,0,0.9)",
              whiteSpace: "nowrap",
            }}
          >
            {scene.title}
          </div>

          {story.tags?.length ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                justifyContent: "center",
                maxWidth: 200,
              }}
            >
              {story.tags.map((tag) => (
                <span
                  key={tag}
                  style={{
                    fontSize: 8,
                    fontWeight: 600,
                    padding: "1px 6px",
                    borderRadius: 999,
                    background: chipBg,
                    border: `1px solid ${chipBorder}`,
                    color: "rgba(232,240,255,0.7)",
                    letterSpacing: 0.3,
                    backdropFilter: "blur(6px)",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Coverbox card — image/placeholder with embedded title + chips      */
/* ------------------------------------------------------------------ */

function CoverboxCard({
  url,
  title,
  contentRating,
  genre,
  realm,
  chipBg,
  chipBorder,
  scene,
  creator,
  creatorAvatarUrl,
}: {
  url: string | null;
  title: string;
  contentRating?: string | null;
  genre?: string | null;
  realm: "cosmos" | "wilds";
  chipBg: string;
  chipBorder: string;
  scene: SceneListItem;
  creator: { id: string; name: string } | null;
  creatorAvatarUrl: string | null;
}) {
  const navigate = useNavigate();
  const isWilds = realm === "wilds";
  const borderColor = isWilds ? "rgba(90,180,90,0.3)" : "rgba(126,184,255,0.3)";
  const hasChips = !!(contentRating || genre);
  const showDefaultSlate =
    scene.is_title && !scene.media_id;

  if (showDefaultSlate) {
    return (
      <div
        style={{
          width: 120,
          minHeight: 140,
          borderRadius: "8px 8px 0 0",
          overflow: "hidden",
          flexShrink: 0,
          position: "relative",
          border: `1px solid ${borderColor}`,
          borderBottom: "none",
          background: "#000",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 8,
          pointerEvents: "auto",
        }}
      >
        <DefaultSceneSlate
          variant={scene.is_title ? "title" : "end"}
          storyTitle={title}
          directorName={creator?.name ?? null}
          directorId={creator?.id ?? null}
          directorAvatarUrl={creatorAvatarUrl}
          realm={realm}
          interactive
          compact
          backgroundUrl={url}
          onDirectorClick={(id) => navigate(`/directors/${id}`)}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        width: 120,
        minHeight: 140,
        borderRadius: "8px 8px 0 0",
        overflow: "hidden",
        flexShrink: 0,
        position: "relative",
        border: `1px solid ${borderColor}`,
        borderBottom: "none",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Background — image or atmospheric placeholder */}
      {url ? (
        <img
          src={url}
          alt={title || "Cover"}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: isWilds
              ? "radial-gradient(ellipse at 30% 20%, rgba(60,140,60,0.25) 0%, rgba(8,20,10,0.92) 70%)"
              : "radial-gradient(ellipse at 30% 20%, rgba(80,120,200,0.25) 0%, rgba(10,14,30,0.92) 70%)",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage:
                "radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 100%), " +
                "radial-gradient(1px 1px at 70% 15%, rgba(255,255,255,0.3) 0%, transparent 100%), " +
                "radial-gradient(1px 1px at 45% 65%, rgba(255,255,255,0.25) 0%, transparent 100%), " +
                "radial-gradient(1px 1px at 80% 75%, rgba(255,255,255,0.35) 0%, transparent 100%), " +
                "radial-gradient(1px 1px at 10% 80%, rgba(255,255,255,0.2) 0%, transparent 100%)",
              pointerEvents: "none",
            }}
          />
        </div>
      )}

      {/* Scrim gradient at bottom so text is always readable */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "70%",
          background: "linear-gradient(to top, rgba(4,7,13,0.85) 0%, transparent 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Content overlay — pushed to bottom */}
      <div style={{ flex: 1 }} />
      <div
        style={{
          position: "relative",
          padding: "6px 8px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 5,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "#e8f0ff",
            textShadow: "0 1px 8px rgba(0,0,0,0.9)",
            lineHeight: 1.25,
            textAlign: "center",
            wordBreak: "break-word",
          }}
        >
          {title || "Untitled"}
        </div>

        {hasChips ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 4,
              justifyContent: "center",
            }}
          >
            {contentRating ? (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  padding: "1px 6px",
                  borderRadius: 999,
                  background: chipBg,
                  border: `1px solid ${chipBorder}`,
                  color: "#e8f0ff",
                  letterSpacing: 0.3,
                  backdropFilter: "blur(6px)",
                }}
              >
                {contentRating}
              </span>
            ) : null}
            {genre ? (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  padding: "1px 6px",
                  borderRadius: 999,
                  background: chipBg,
                  border: `1px solid ${chipBorder}`,
                  color: "#e8f0ff",
                  letterSpacing: 0.3,
                  backdropFilter: "blur(6px)",
                }}
              >
                {genre}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Control strip — edit / delete as attached sci-fi panel buttons      */
/* ------------------------------------------------------------------ */

function ControlStrip({
  realm,
  accent,
  accentSolid,
  previewWidth,
  attached = false,
  onEdit,
  canLaunch,
  onLaunch,
  canPublish,
  onPublish,
  canUnpublish = false,
  onUnpublishClick,
  unpublishState = "idle",
  canRetract = false,
  onRetract,
  canDelete,
  deleteState,
  onDeleteClick,
}: {
  realm: "cosmos" | "wilds";
  accent: string;
  accentSolid: string;
  previewWidth: number;
  attached?: boolean;
  onEdit?: () => void;
  canLaunch: boolean;
  onLaunch?: () => void;
  canPublish: boolean;
  onPublish?: () => void;
  canUnpublish?: boolean;
  onUnpublishClick?: () => void;
  unpublishState?: DeleteState;
  canRetract?: boolean;
  onRetract?: () => void;
  canDelete: boolean;
  deleteState: DeleteState;
  onDeleteClick: () => void;
}) {
  const isWilds = realm === "wilds";
  const panelBg = isWilds
    ? "linear-gradient(to bottom, rgba(20,38,20,0.92), rgba(12,24,12,0.96))"
    : "linear-gradient(to bottom, rgba(16,22,38,0.92), rgba(8,12,24,0.96))";
  const panelBorder = isWilds ? "rgba(90,180,90,0.3)" : "rgba(126,184,255,0.3)";
  const dividerColor = isWilds ? "rgba(90,180,90,0.15)" : "rgba(126,184,255,0.15)";

  const slots: Array<{
    key: string;
    label: string;
    icon: "edit" | "delete" | "confirm" | "publish" | "launch";
    onClick: () => void;
    isDestructive: boolean;
    isConfirming: boolean;
  } | null> = [
    onEdit
      ? {
          key: "edit",
          label: t(realm, "hud_edit_control"),
          icon: "edit",
          onClick: onEdit,
          isDestructive: false,
          isConfirming: false,
        }
      : null,
    canLaunch && onLaunch
      ? {
          key: "launch",
          label: t(realm, "hud_launch_control", "Launch"),
          icon: "launch",
          onClick: onLaunch,
          isDestructive: false,
          isConfirming: false,
        }
      : null,
    canPublish && onPublish
      ? {
          key: "publish",
          label: t(realm, "editor_publish_story", "Publish"),
          icon: "publish",
          onClick: onPublish,
          isDestructive: false,
          isConfirming: false,
        }
      : null,
    canUnpublish && onUnpublishClick
      ? {
          key: "unpublish",
          label:
            unpublishState === "confirming"
              ? "?"
              : t(realm, "hud_unpublish_control"),
          icon: unpublishState === "confirming" ? "confirm" : "delete",
          onClick: onUnpublishClick,
          isDestructive: true,
          isConfirming: unpublishState === "confirming",
        }
      : null,
    canRetract && onRetract
      ? {
          key: "retract",
          label: t(realm, "hud_retract_control"),
          icon: "delete",
          onClick: onRetract,
          isDestructive: true,
          isConfirming: false,
        }
      : null,
    canDelete
      ? {
          key: "delete",
          label:
            deleteState === "confirming"
              ? "?"
              : t(realm, "hud_delete_control"),
          icon: deleteState === "confirming" ? "confirm" : "delete",
          onClick: onDeleteClick,
          isDestructive: true,
          isConfirming: deleteState === "confirming",
        }
      : null,
  ];

  const buttons = slots.filter(Boolean) as NonNullable<(typeof slots)[number]>[];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        width: previewWidth,
        background: panelBg,
        borderRadius: attached ? "0 0 6px 6px" : 6,
        border: `1px solid ${panelBorder}`,
        borderTop: attached ? "none" : `1px solid ${panelBorder}`,
        overflow: "hidden",
        gap: 0,
      }}
    >
      {buttons.map((btn, index) => (
        <PanelButton
          key={btn.key}
          realm={realm}
          accent={accent}
          accentSolid={accentSolid}
          label={btn.label}
          icon={btn.icon}
          onClick={btn.onClick}
          isDestructive={btn.isDestructive}
          isConfirming={btn.isConfirming}
          hasDividerRight={index % 2 === 0 && index < buttons.length - 1}
          hasDividerBottom={index < buttons.length - 2}
          dividerColor={dividerColor}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Individual panel button — lever/switch aesthetic                    */
/* ------------------------------------------------------------------ */

function PanelButton({
  realm,
  accent,
  accentSolid,
  label,
  icon,
  onClick,
  isDestructive,
  isConfirming,
  hasDividerRight,
  hasDividerBottom,
  dividerColor,
}: {
  realm: "cosmos" | "wilds";
  accent: string;
  accentSolid: string;
  label: string;
  icon: "edit" | "delete" | "confirm" | "publish" | "launch";
  onClick: () => void;
  isDestructive: boolean;
  isConfirming: boolean;
  hasDividerRight: boolean;
  hasDividerBottom: boolean;
  dividerColor: string;
}) {
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);

  const activeColor = isConfirming ? "#ff6b6b" : accentSolid;
  const glowColor = isConfirming
    ? "rgba(255,80,80,0.4)"
    : realm === "wilds"
      ? "rgba(120,200,120,0.3)"
      : "rgba(126,184,255,0.3)";

  const confirmBg = isConfirming ? "rgba(255,60,60,0.15)" : "transparent";

  return (
    <button
      onClick={() => {
        setPressed(true);
        setTimeout(() => setPressed(false), 150);
        onClick();
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        minHeight: 44,
        pointerEvents: "auto",
        background: confirmBg,
        border: "none",
        borderRight: hasDividerRight ? `1px solid ${dividerColor}` : "none",
        borderBottom: hasDividerBottom ? `1px solid ${dividerColor}` : "none",
        cursor: "pointer",
        padding: "4px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        transition: "transform 0.15s ease, box-shadow 0.2s ease, background 0.2s",
        transform: pressed
          ? "translateY(1px) scale(0.95)"
          : hovered
            ? "translateY(-1px)"
            : "none",
        boxShadow: hovered ? `0 0 8px ${glowColor}` : "none",
      }}
    >
      {icon === "edit" ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          style={{
            opacity: hovered ? 0.95 : 0.55,
            transition: "opacity 0.2s",
            filter: hovered ? `drop-shadow(0 0 3px ${glowColor})` : "none",
          }}
        >
          <path
            d="M11.5 1.5l3 3-9 9H2.5v-3l9-9zM10 3l3 3"
            stroke={activeColor}
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : icon === "publish" ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          style={{
            opacity: hovered ? 0.95 : 0.55,
            transition: "opacity 0.2s",
            filter: hovered ? `drop-shadow(0 0 3px ${glowColor})` : "none",
          }}
        >
          <path
            d="M8 2v8M4.5 6.5L8 2l3.5 4.5M3 12h10"
            stroke={activeColor}
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : icon === "launch" ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          style={{
            opacity: hovered ? 0.95 : 0.55,
            transition: "opacity 0.2s",
            filter: hovered ? `drop-shadow(0 0 3px ${glowColor})` : "none",
          }}
        >
          <path
            d="M8 13V4M5 7l3-3 3 3M4 13h8"
            stroke={activeColor}
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : icon === "delete" ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          style={{
            opacity: hovered ? 0.95 : 0.55,
            transition: "opacity 0.2s",
            filter: hovered ? `drop-shadow(0 0 3px ${glowColor})` : "none",
          }}
        >
          <path
            d="M5.5 1.5h5M2 3.5h12M3.5 3.5l.7 9.1a1.5 1.5 0 001.5 1.4h4.6a1.5 1.5 0 001.5-1.4l.7-9.1M6.5 6.5v4M9.5 6.5v4"
            stroke={activeColor}
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <span
          style={{
            fontSize: 13,
            fontWeight: 800,
            lineHeight: 1,
            color: "#ff6b6b",
            textShadow: "0 0 8px rgba(255,80,80,0.6)",
          }}
        >
          ?
        </span>
      )}
      <span
        style={{
          fontSize: 8,
          fontWeight: 600,
          color: isConfirming ? "#ff6b6b" : "rgba(232,240,255,0.5)",
          letterSpacing: 0.6,
          textTransform: "uppercase",
          lineHeight: 1,
          transition: "color 0.2s",
          ...(hovered && !isConfirming ? { color: "rgba(232,240,255,0.8)" } : {}),
        }}
      >
        {label}
      </span>
    </button>
  );
}
